import MyTable, { ColumnHeader, RowData } from "@/components/MyTable";
import { Pagination } from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import { getStatusBadge } from "@/utils/helpers";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { TriangleAlert } from "lucide-react";
import { patientV2InvalidBadgeClass } from "./patient-v2/constants";

const CRITICAL_ALERT_COL_ID = "__criticalAlert";

function isCriticalRow(row: RowData) {
  return String(row?.status ?? "").toLowerCase() === "critical";
}

function isInvalidRow(row: RowData) {
  return !!(row as { invalid?: boolean })?.invalid;
}

function withCriticalAlertCell(rows: RowData[]): RowData[] {
  return rows.map((row) => {
    const isCritical = isCriticalRow(row);
    const isInvalid = isInvalidRow(row);
    const invalidBadge = getStatusBadge("Invalid");

    if (!isCritical && !isInvalid) {
      return { ...row, [CRITICAL_ALERT_COL_ID]: "" };
    }

    return {
      ...row,
      [CRITICAL_ALERT_COL_ID]: (
        <span className="inline-flex w-full items-center justify-end gap-2">
          {isInvalid ? (
           <Badge className={`${patientV2InvalidBadgeClass} uppercase`}>
           Invalid
         </Badge>
          ) : null}
          {isCritical ? (
            <span
              className="inline-flex shrink-0"
              role="img"
              aria-label="Critical"
            >
              <TriangleAlert
                className="h-5 w-5 shrink-0 text-destructive"
                aria-hidden
              />
            </span>
          ) : null}
        </span>
      ),
    };
  });
}

export type ClientPaginatedMyTableProps = {
  columnHeaders: ColumnHeader[];
  /** Full dataset in display order. */
  columnsData: RowData[];
  /** Optional rows for display; must align index-for-index with `columnsData` when both are set. */
  viewColumnData?: RowData[];
  /** Shows `MyTable` skeleton rows when true. */
  loading?: boolean;
  defaultLimit?: number;
  clickRow?: (row: RowData) => void;
  className?: string;
};

/**
 * Renders `MyTable` with local slicing and the shared `Pagination` control.
 * No API calls — pass the full `columnsData` (and optional `viewColumnData`) from the parent.
 */
const ClientPaginatedMyTable: React.FC<ClientPaginatedMyTableProps> = ({
  columnHeaders,
  columnsData,
  viewColumnData,
  loading = false,
  defaultLimit = 10,
  clickRow = () => {},
  className,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  const totalRows = columnsData?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRows / limit));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const { slicedColumns, slicedView } = useMemo(() => {
    const start = (page - 1) * limit;
    const cols = (columnsData || []).slice(start, start + limit);
    const source = viewColumnData ?? columnsData;
    const view = (source || []).slice(start, start + limit);
    return { slicedColumns: cols, slicedView: view };
  }, [columnsData, viewColumnData, page, limit]);

  const tableHeaders = useMemo(() => {
    const hasAlert = columnHeaders.some((h) => h.id === CRITICAL_ALERT_COL_ID);
    if (hasAlert) return columnHeaders;
    return [
      ...columnHeaders,
      {
        id: CRITICAL_ALERT_COL_ID,
        label: "",
        isSortable: false,
      } satisfies ColumnHeader,
    ];
  }, [columnHeaders]);

  const columnOrder = useMemo(
    () => tableHeaders.map((h) => h.id),
    [tableHeaders],
  );

  const columnsWithAlert = useMemo(
    () => withCriticalAlertCell(slicedColumns),
    [slicedColumns],
  );
  const viewWithAlert = useMemo(
    () => withCriticalAlertCell(slicedView),
    [slicedView],
  );

  return (
    <div
      className={clsx(
        "space-y-4 rounded-md border border-grey pb-2",
        className,
      )}
    >
      <MyTable
        className="min-w-0"
        columnHeaders={tableHeaders}
        columnOrder={columnOrder}
        loading={loading}
        limit={limit}
        columnsData={columnsWithAlert}
        viewColumnData={viewWithAlert}
        isActionColumn={false}
        actionFn={() => {}}
        sortFn={() => {}}
        isSelectable={false}
        selected={[]}
        setSelected={() => {}}
        clickRow={clickRow}
        moreActions={[]}
        clickCell={[]}
      />
      {!loading && (
        <div className="px-2">
          <Pagination
            page={page}
            limit={limit}
            setLimit={setLimit}
            setPage={setPage}
            total={pageCount}
          />
        </div>
      )}
    </div>
  );
};

export default ClientPaginatedMyTable;
